<template>
  <div class="container-fluid print-maps-page">
    <div ref="mobileCtaRef" class="mobileCTA d-flex justify-content-center align-items-center p-3">
      <div class="text-center d-flex flex-column align-items-center justify-content-center" id="app-cta-overlay">
        <img class="rounded mb-3" :alt="$t('print_maps.hiiker_app')" width="250" src="../../images/app-hero-demo.webp">
        <h3><strong>{{ $t('print_maps.printing_from_your_mobile_device') }}</strong></h3>
        <p><small>{{ $t('print_maps.get_the_best_experience_on_the') }}</small></p>
        <a class="btn btn-primary mt-2" href="/app"><span>{{ $t('shared.open_in_app') }}</span></a>
      </div>
    </div>

    <div class="row print-maps-row">
      <div class="col-12 col-sm-8 col-xl-9 d-flex position-relative bg-gray print-left-col">
        <img class="img-fluid home-stats-bg" src="../../images/map-lines.webp" :alt="$t('print_maps.hiiker_contours_2')" :title="$t('print_maps.hiiker_contours')">
        <div class="map-container d-flex justify-content-center align-items-center align-self-center">
          <div class="d-flex flex-column align-items-center w-100 h-100">
            <!-- Top left, opposite the print queue. Deliberately labelled
                 rather than a bare chevron: this page is reached from a hike
                 map, the plan screen, a recording, a waypoint group and the
                 explore map, so an icon alone would not say what leaving
                 does. -->
            <div class="back-container">
              <button
                type="button"
                class="btn btn-sm rounded-pill d-flex align-items-center back-button"
                @click="goBack"
              >
                <div class="d-flex align-items-center justify-content-center pr-1">
                  <span class="material-icons">arrow_back</span>
                </div>
                <div>{{ $t('shared.back') }}</div>
              </button>
            </div>

            <div class="button-container">
              <div class="trail-map-buttons">
                <button class="btn btn-sm rounded-pill d-flex align-items-center" @click="buttonClicked(2)">
                  <div class="d-flex align-items-center justify-content-center">
                    <span class="material-icons">search</span>
                  </div>
                </button>
                <button class="btn btn-sm rounded-pill d-flex align-items-center" @click="buttonClicked(0)">
                  <div class="d-flex align-items-center justify-content-center pr-1">
                    <span class="material-icons">layers</span>
                  </div>
                  <div>{{ $t('shared.maps') }}</div>
                </button>
              </div>
            </div>

            <div class="queue-container">
              <PrintQueueDrawer ref="queueDrawerRef"/>
            </div>

            <div class="map-content ratio-1x1">
              <div id="map" ref="mapContainerRef" class="map-custom-class"></div>
            </div>

            <div class="sheet-readout" v-if="mapInstance">
              <div class="readout-scale">{{ formattedScale }}</div>
              <dl class="readout-facts">
                <template v-if="readoutDenominator">
                  <dt>{{ $t('print_maps.covers') }}</dt>
                  <dd>{{ coverageSize }}</dd>
                </template>
                <dt>{{ $t('print_maps.style') }}</dt>
                <dd>{{ mapStyleLabel }}</dd>
                <template v-if="gridNorthNote">
                  <dt>{{ $t('print_maps.grid') }}</dt>
                  <dd>{{ gridNorthNote }}</dd>
                </template>
              </dl>
            </div>
          </div>

          <WaypointDialog />
        </div>
        <MapControls />
      </div>

      <div class="col-12 col-sm-4 col-xl-3 d-flex flex-column print-right-col">
        <div class="options-scroll">
          <h6 class="option-heading">{{ $t('shared.title') }}</h6>
          <input type="text" v-model="mapTitle" :placeholder="$t('print_maps.enter_map_title')" class="form-control form-control-sm rounded-pill mb-3">

          <h6 class="option-heading">{{ $t('print_maps.page') }}</h6>
          <div class="btn-group w-100 mb-2" role="group">
            <button
              v-for="size in pageSizes" :key="size"
              class="btn btn-outline-primary btn-sm text-uppercase"
              :class="{ active: options.pageSize === size }"
              @click="setPageSize(size)"
            >{{ size }}</button>
          </div>
          <div class="btn-group w-100 mb-3" role="group">
            <button
              class="btn btn-outline-primary btn-sm"
              :class="{ active: options.orientation === 'portrait' }"
              @click="setOrientation('portrait')"
            >{{ $t('print_maps.portrait') }}</button>
            <button
              class="btn btn-outline-primary btn-sm"
              :class="{ active: options.orientation === 'landscape' }"
              @click="setOrientation('landscape')"
            >{{ $t('print_maps.landscape') }}</button>
          </div>

          <h6 class="option-heading">{{ $t('print_maps.scale') }}</h6>
          <!-- "Fit" used to be a chip in this row, which made it look like a
               scale called Fit. It is not a scale, it is the absence of one —
               so it gets its own control, and the row below always shows the
               number it lands on. -->
          <div v-if="autoScaleAvailable" class="btn-group w-100 mb-2" role="group">
            <button
              class="btn btn-outline-primary btn-sm"
              :class="{ active: isFittedScale }"
              @click="setScale(null)"
            >{{ $t('print_maps.fit_to_the_walk') }}</button>
            <button
              class="btn btn-outline-primary btn-sm"
              :class="{ active: !isFittedScale }"
              @click="setScale(lastFixedDenominator)"
            >{{ $t('print_maps.fixed_scale') }}</button>
          </div>
          <!-- Dimmed rather than disabled while fitting: picking a scale is an
               unambiguous "I want a fixed one", so it switches for you instead
               of ignoring the click. -->
          <div class="scale-buttons d-flex gap-1 flex-wrap mb-1" :class="{ 'is-fitted': isFittedScale }">
            <button
              v-for="value in pickerScales" :key="value"
              class="btn btn-outline-primary btn-sm scale-chip"
              :class="{ active: options.scaleDenominator === value }"
              @click="setScale(value)"
            >{{ shortScale(value) }}</button>
          </div>
          <p class="option-hint mb-3">{{ scaleHint }}</p>

          <!-- Everything below is a considered choice rather than a normal one:
               folded away so the panel reads page → scale → print, but
               summarised on the button so a switch left on is still visible
               from the outside. `v-show`, not `v-if` — remounting would drop
               the date field's state and re-run the overlay watchers. -->
          <button
            type="button"
            class="advanced-toggle"
            :aria-expanded="advancedOpen"
            aria-controls="print-advanced"
            @click="advancedOpen = !advancedOpen"
          >
            <span class="advanced-toggle-text">
              <span class="advanced-title">{{ $t('print_maps.advanced_settings') }}</span>
              <span class="advanced-summary">{{ advancedSummary }}</span>
            </span>
            <span class="material-icons advanced-chevron" :class="{ open: advancedOpen }">expand_more</span>
          </button>

          <div v-show="advancedOpen" id="print-advanced" class="advanced-body">
            <h6 class="option-heading">{{ $t('print_maps.grid_amp_projection') }}</h6>
            <select class="form-select form-select-sm mb-2" v-model="options.projection" @change="onProjectionChange">
              <option v-for="projection in projections" :key="projection.key" :value="projection.key">
                {{ projection.label }}
              </option>
            </select>
            <p v-if="projectionWarning" class="option-warning mb-2">{{ projectionWarning }}</p>
            <div class="form-check form-switch mb-3">
              <input class="form-check-input" type="checkbox" id="opt-grid" v-model="options.showGrid" @change="refreshSheetOverlay">
              <label class="form-check-label" for="opt-grid">{{ $t('print_maps.show_grid_amp_edge_coordinates') }}</label>
            </div>

            <h6 class="option-heading">{{ $t('print_maps.on_every_sheet') }}</h6>
            <!-- These three depend on there being a route or a hike behind
                 the sheet. Absent one, they were permanently greyed switches
                 explaining why they could not be used — clutter that answers
                 a question nobody asked. -->
            <div v-if="hasRoute" class="form-check form-switch">
              <input class="form-check-input" type="checkbox" id="opt-profile" v-model="options.showElevationProfile" @change="refreshSheetOverlay">
              <label class="form-check-label" for="opt-profile">{{ $t('shared.elevation_profile') }}</label>
            </div>
            <div class="form-check form-switch">
              <input class="form-check-input" type="checkbox" id="opt-declination" v-model="options.showDeclination">
              <label class="form-check-label" for="opt-declination">{{ $t('print_maps.magnetic_declination') }}</label>
            </div>
            <div class="form-check form-switch">
              <input class="form-check-input" type="checkbox" id="opt-scalebar" v-model="options.showScaleBar">
              <label class="form-check-label" for="opt-scalebar">{{ $t('print_maps.scale_bar') }}</label>
            </div>
            <div class="form-check form-switch">
              <input class="form-check-input" type="checkbox" id="opt-legend" v-model="options.showLegend">
              <label class="form-check-label" for="opt-legend">{{ $t('print_maps.legend') }}</label>
            </div>
            <div class="form-check form-switch">
              <input class="form-check-input" type="checkbox" id="opt-heatmap" v-model="options.showHeatmap">
              <label class="form-check-label" for="opt-heatmap">
                {{ $t('print_maps.community_heatmap') }}
                <small class="text-muted d-block">{{ $t('print_maps.community_heatmap_hint') }}</small>
              </label>
            </div>
            <div v-if="source" class="form-check form-switch">
              <input class="form-check-input" type="checkbox" id="opt-qr" v-model="options.showQrCode">
              <label class="form-check-label" for="opt-qr">{{ $t('print_maps.qr_code_to_the_hike') }}</label>
            </div>
            <div v-if="source" class="form-check form-switch mb-3">
              <input class="form-check-input" type="checkbox" id="opt-info" v-model="options.showInfoSheet">
              <label class="form-check-label" for="opt-info">
                {{ $t('print_maps.information_sheet') }}
                <small class="text-muted d-block">{{ $t('print_maps.an_extra_page_listing_the_days') }}</small>
              </label>
            </div>

            <h6 class="option-heading">{{ $t('print_maps.date') }}</h6>
            <input type="date" class="form-control form-control-sm mb-2" v-model="options.hikeDate">
            <div class="form-check form-switch mb-3">
              <input class="form-check-input" type="checkbox" id="opt-sun" v-model="options.showSunTimes" :disabled="!options.hikeDate">
              <label class="form-check-label" for="opt-sun">
                {{ $t('print_maps.sunrise_sunset') }}
                <small v-if="!options.hikeDate" class="text-muted d-block">{{ $t('print_maps.pick_a_date_first') }}</small>
                <small v-else class="text-muted d-block">{{ options.timeZone }}</small>
              </label>
            </div>
          </div>

          <!-- The route line, folded the same way as Advanced but kept
               separate: it is the one setting here that is about how the map
               looks rather than what the page contains, and it is shared with
               the hike map and the apps rather than being a property of this
               sheet.

               Only offered when there is a line to style, matching the
               elevation-profile switch above: on a hand-framed area with no
               route the whole editor — preview, palette, thickness — would
               change nothing you can see on the sheet in front of you.

               `v-show` inside, not `v-if` — remounting resets the editor's
               tab back to Main. -->
          <template v-if="hasRoute">
            <button
              type="button"
              class="advanced-toggle"
              :aria-expanded="routeStyleOpen"
              aria-controls="print-route-style"
              @click="routeStyleOpen = !routeStyleOpen"
            >
              <span class="advanced-toggle-text">
                <span class="advanced-title">{{ $t('print_maps.customise_route_style') }}</span>
                <span class="advanced-summary">{{ routeStyleSummaryText }}</span>
              </span>
              <span class="material-icons advanced-chevron" :class="{ open: routeStyleOpen }">expand_more</span>
            </button>

            <div v-show="routeStyleOpen" id="print-route-style" class="route-style-body">
              <p class="option-hint">{{ $t('print_maps.applies_to_the_printed_sheet_the') }}</p>
              <CustomiseRouteStyleView compact />
            </div>
          </template>

          <!-- Last, not first: it sits directly above the button that acts on
               it, so "Print all 9 days" and the choice of what those days are
               read as one thing. Deliberately outside the Advanced fold — it
               is the one question nobody should have to go looking for.

               Hidden entirely without a source: a radio group offering one
               option is not a question, and "This map area" alone just
               restates what the map in front of you already is. -->
          <div v-if="hasModeChoice" class="mode-card">
            <div v-if="source" class="source-name">{{ source.name }}</div>
            <div v-if="source?.multiDay" class="source-meta">{{ $t('print_maps.days_day_itinerary', { days: source.days.length }) }}</div>

            <h6 class="option-heading mt-2">{{ $t('print_maps.what_to_print') }}</h6>
            <div class="mode-options">
              <label v-for="mode in availableModes" :key="mode.value" class="mode-option" :class="{ active: printMode === mode.value }">
                <input type="radio" name="print-mode" :value="mode.value" v-model="printMode">
                <span>
                  <span class="mode-label">{{ mode.label }}</span>
                  <small class="mode-hint">{{ mode.hint }}</small>
                </span>
              </label>
            </div>
          </div>

          <!-- The pile only exists for hand-framed sheets; a whole-hike job is
               one action, not something you collect. -->
          <div v-if="isCustomArea && pile.length" class="pile mb-3">
            <h6 class="option-heading d-flex justify-content-between align-items-center">
              <span>{{ $t('print_maps.sheets_to_print_pile', { pile: pile.length }) }}</span>
              <button class="btn btn-sm btn-link p-0 text-muted" @click="pile = []">{{ $t('shared.clear') }}</button>
            </h6>
            <div v-for="(sheet, index) in pile" :key="sheet.key" class="pile-item">
              <div class="pile-index">{{ index + 1 }}</div>
              <div class="pile-body">
                <div class="pile-title">{{ sheet.title }}</div>
                <div class="pile-meta">{{ describeSheet(sheet) }}</div>
              </div>
              <button class="btn btn-sm btn-link text-muted p-0" @click="removeSheet(sheet.key)" :aria-label="$t('print_maps.remove_sheet')">
                <span class="material-icons" style="font-size: 1.1rem">close</span>
              </button>
            </div>
          </div>
        </div>

        <div class="print-actions">
          <button
            v-if="isCustomArea"
            class="btn btn-outline-primary w-100 mb-2 btn-sm"
            @click="addCurrentSheet"
          >
            <span class="material-icons align-middle me-1" style="font-size: 1rem">add</span>
            {{ $t('print_maps.add_another_sheet_at_this_spot') }}
          </button>
          <button class="btn btn-primary w-100 d-flex justify-content-center" :disabled="submitting" @click="printNow">
            <span v-if="submitting" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            {{ primaryActionLabel }}
          </button>
          <p class="action-hint">{{ primaryActionHint }}</p>
          <p v-if="flash" class="option-flash" :class="flash.type">{{ flash.message }}</p>
        </div>
      </div>

      <Teleport to="body">
        <BaseMapStylesDialog />
        <GoToLocationDialog />
        <PrintMapStyleHintDialog />
      </Teleport>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import { useStore } from 'vuex';
import mapboxgl, { Map, LngLatLike } from 'mapbox-gl';
import type { GeoJSONSource } from 'mapbox-gl';
import { Modal } from 'bootstrap';

import MapControls from '../../apps/components/MapControls.vue';
import GoToLocationDialog from '../../apps/go_to_location_dialog/GoToLocationDialog.vue';
import BaseMapStylesDialog from '../../apps/base_map_dialog/BaseMapStylesDialog.vue';
import CustomiseRouteStyleView from '../../apps/base_map_dialog/components/CustomiseRouteStyleView.vue';
import WaypointDialog from '../../apps/waypoint_dialog/WaypointDialog.vue';
import PrintQueueDrawer from './PrintQueueDrawer.vue';
import PrintMapStyleHintDialog from './PrintMapStyleHintDialog.vue';

import { RevenueCatWrapper } from '../../utils/RevenueCatWrapper';
import { HiikerMapStyle, SavedCenter } from '../../utils/models/MapStyles';
import { OnBoardingGoal } from '../../utils/models/OnBoardingGoal';
import getQueryParams from '../../utils/helpers/GetQueryParamsHelper';
import createGeoLocate from '../../utils/helpers/GeoLocateHelper';
import { LayerRegistry } from '../../utils/layer-registry';
import emitter from '../../utils/bus';
import { getRouteStyleSettings } from '../../utils/routeStyleSettings';
import { applyPrintPreviewRouteStyle } from '../../utils/printMapRouteStyleExpressions';
import { i18n } from '../../i18n';

const t = i18n.global.t;

import {
  markPrintMapStyleHintSeen,
  shouldShowPrintMapStyleHint,
} from '../../utils/print_maps/mapStyleHintPreference';
import { printMapBackTarget } from '../../utils/print_maps/printMapBackTarget';
import {
  DEFAULT_SCALE_DENOMINATOR,
  PAGE_SIZES,
  PICKER_SCALES,
  formatScale,
  sheetCoverageKm,
  sheetLayout,
  sheetPolygon,
} from '../../utils/print_maps/printMapGeometry';
import type { Orientation, PageSize } from '../../utils/print_maps/printMapGeometry';
import {
  PROJECTIONS,
  bearingDelta,
  findProjection,
  gridNorthBearing,
  isUsableAt,
  signedBearing,
} from '../../utils/print_maps/printMapProjections';
import {
  DEFAULT_OPTIONS,
  buildBatchVariables,
  buildPileVariables,
  dateInputValue,
  describeAdvancedOptions,
  describeSheet,
  optionsForMode,
  outlineCollection,
  pileJobCount,
  resolvePrintMapType,
} from '../../utils/print_maps/printSheetQueue';
import type { PrintContext, QueuedSheet, SheetMode, SheetOptions } from '../../utils/print_maps/printSheetQueue';
import { daysOverOneSheet, fittedDenominators } from '../../utils/print_maps/printScalePlan';
import { initialShowHeatmap, syncPrintHeatmap } from '../../utils/print_maps/printHeatmap';
import {
  availableModes as buildAvailableModes,
  combinedSuccessMessage as buildCombinedSuccessMessage,
  coverageHint as buildCoverageHint,
  failedSheetCount as buildFailedSheetCount,
  gridNorthNote as buildGridNorthNote,
  hasModeChoice as anyModeChoice,
  perDayHint as buildPerDayHint,
  primaryActionHint as buildPrimaryActionHint,
  primaryActionLabel as buildPrimaryActionLabel,
  readoutScale,
  routeStyleSummary,
  scaleHint as buildScaleHint,
  shortScale as buildShortScale,
} from '../../utils/print_maps/printPanelCopy';

interface PrintMapSource {
  type: string;
  id: number | string;
  name: string;
  url?: string | null;
  multiDay: boolean;
  days: Array<{ id: number; dayNumber: number; name: string; distanceMeters: number; elevationMeters: number }>;
}

const store = useStore();

const mapContainerRef = ref<HTMLDivElement | null>(null);
const mobileCtaRef = ref<HTMLDivElement | null>(null);
const queueDrawerRef = ref<InstanceType<typeof PrintQueueDrawer> | null>(null);
const mapInstance = ref<Map | null>(null);

const windowWithExtras = window as typeof window & {
  defaultPrintMapTitle?: string;
  printMapSource?: PrintMapSource;
  printMapOptions?: { gridCrs?: string };
};

const defaultMapTitle = windowWithExtras.defaultPrintMapTitle ?? '';
const source = ref<PrintMapSource | null>(windowWithExtras.printMapSource ?? null);
const mapTitle = ref<string>(defaultMapTitle);
const pile = ref<QueuedSheet[]>([]);
// What the user is printing. Chosen first, because it decides whether the
// map framing matters at all and what the one action at the foot of the
// panel does.
const printMode = ref<SheetMode>('single');
const submitting = ref(false);
const flash = ref<{ type: 'ok' | 'error'; message: string } | null>(null);
const sheetCounter = ref(0);

const pageSizes = PAGE_SIZES;
const pickerScales = PICKER_SCALES;
const projections = PROJECTIONS;

function safeLocalStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

const options = reactive<SheetOptions>({
  ...DEFAULT_OPTIONS,
  scaleDenominator: DEFAULT_SCALE_DENOMINATOR,
  gridCrs: windowWithExtras.printMapOptions?.gridCrs ?? 'LatLon',
  showQrCode: Boolean(source.value),
  // Follows the main map's heatmap overlay. localStorage can throw on access
  // (private browsing), which must not break the page.
  showHeatmap: initialShowHeatmap(safeLocalStorage()),
  // Today, because that is what someone printing a map is overwhelmingly
  // about to walk. They can change it; they no longer have to set it before
  // the sunrise/sunset switch will even enable.
  hikeDate: dateInputValue(new Date()),
  // The browser's zone is the honest default: the sheet always prints the
  // zone name next to the times, so it can never be read out of context.
  timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
});

const advancedOpen = ref(false);
const routeStyleOpen = ref(false);
// Mirrored into a ref so the fold's summary re-renders on an edit: the
// settings live in localStorage, which Vue cannot track. The editor emits
// `route-style-settings:changed` on every change, and so does the cloud sync
// when a value arrives from another device.
const routeStyleSettings = ref(getRouteStyleSettings());
// Remembered so switching Fit → Fixed → Fit → Fixed comes back to the scale
// they last chose rather than resetting to the default every time.
const lastFixedDenominator = ref<number>(DEFAULT_SCALE_DENOMINATOR);

const EMPTY_COLLECTION: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] };
const SHEET_SOURCE_ID = 'print-sheet-preview';
const PILE_SOURCE_ID = 'print-sheet-pile';

const layout = computed(() =>
  sheetLayout({
    pageSize: options.pageSize,
    orientation: options.orientation,
    showGrid: options.showGrid,
    showElevationProfile: options.showElevationProfile,
  }),
);

const isFittedScale = computed(() => options.scaleDenominator === null);

/**
 * What "fit to the walk" will actually print at.
 *
 * One value for a whole-route job; one per day for a fan-out, which is where
 * the surprise lives — those days do not all come out at the same scale.
 */
const fittedScales = computed(() => {
  if (!isFittedScale.value || isCustomArea.value) return [];

  return fittedDenominators(
    store.state.printMap.featureCollection,
    layout.value.frame.width,
    layout.value.frame.height,
    printMode.value === 'per_day',
  );
});

// Naming the fitted scale on the map readout is the whole point: it is the
// only place the number appeared before the PDF landed.
const formattedScale = computed(() => readoutScale(options.scaleDenominator, fittedScales.value));

/**
 * Days that will not fit on a single sheet at the scale in play.
 *
 * Fitting chooses a scale per day, so this is nearly always zero there. A
 * fixed scale is where it bites — 1:25,000 on a 25 km day runs to two or
 * three sheets — and the panel promised "one sheet per day" regardless.
 */
const overflowDays = computed(() => daysOverOneSheet(
  store.state.printMap.featureCollection,
  layout.value.frame.width,
  layout.value.frame.height,
  options.scaleDenominator,
));

const perDayHint = computed(() => buildPerDayHint(overflowDays.value));

const availableModes = computed(() => buildAvailableModes(
  source.value ? { multiDay: source.value.multiDay, dayCount: source.value.days.length } : null,
  perDayHint.value,
));

/**
 * Whether there is actually a decision to make. Without a hike or recording
 * behind the page the only mode is "this map area", and a one-option radio
 * group is furniture, not a question.
 */
const hasModeChoice = computed(() => anyModeChoice(availableModes.value));

/** True when the user is framing sheets by hand on the map. */
const isCustomArea = computed(() => printMode.value === 'single');

/** Fitting a scale only means something when there is a route to fit to. */
const autoScaleAvailable = computed(() => !isCustomArea.value);

// Named on the sheet readout so the style that will actually print is visible
// on the page, rather than something you only discover in the PDF.
const mapStyleLabel = computed(() => store.state.mapStyles?.currentMapStyle?.name ?? 'Standard');

// A bare map extent has nothing to profile. Without this the option is
// selectable on an empty sheet, and the layout still reserves the profile
// band — so the map is shrunk to make room for a strip that draws nothing.
const hasRoute = computed(() => {
  if (source.value) return true;
  const features = store.state.printMap.featureCollection?.features;
  return Array.isArray(features) && features.length > 0;
});

/**
 * The single scale this job prints at, or null when there isn't one — a
 * per-day fan-out whose days fit at different scales has no one answer, and
 * the readout says so rather than picking a page's worth at random.
 */
const readoutDenominator = computed(() => {
  if (options.scaleDenominator) return options.scaleDenominator;
  return fittedScales.value.length === 1 ? fittedScales.value[0] : null;
});

const coverageHint = computed(() => buildCoverageHint(
  options.scaleDenominator
    ? sheetCoverageKm(options.scaleDenominator, layout.value.frame.width, layout.value.frame.height)
    : null,
));

const coverageSize = computed(() => {
  if (!readoutDenominator.value) return '—';

  const coverage = sheetCoverageKm(readoutDenominator.value, layout.value.frame.width, layout.value.frame.height);
  return `${coverage.widthKm.toFixed(1)} × ${coverage.heightKm.toFixed(1)} km`;
});

const scaleHint = computed(() => buildScaleHint({
  fitted:       isFittedScale.value,
  mode:         printMode.value,
  coverage:     coverageHint.value,
  fittedScales: fittedScales.value,
  overflow:     overflowDays.value,
}));

const actionInput = computed(() => ({
  mode:     printMode.value,
  dayCount: source.value?.days.length ?? 0,
  pileSize: pile.value.length,
  overflow: overflowDays.value,
  pileJobs: pileJobCount(pile.value),
}));

const primaryActionLabel = computed(() => buildPrimaryActionLabel(actionInput.value));

const primaryActionHint = computed(() => buildPrimaryActionHint(actionInput.value));

// Mirrors the map's centre reactively. `map.getCenter()` is not a reactive
// source, so anything derived from it inside a computed would be evaluated
// once and cached for the life of the page — the grid-north note and the
// projection warning would then describe wherever the user happened to start.
const mapCenter = ref<LngLat>({ lng: 0, lat: 0 });

function syncMapCenter(): void {
  const center = mapInstance.value?.getCenter();
  if (!center) return;
  mapCenter.value = { lng: center.lng, lat: center.lat };
}

const currentBearing = computed(() => gridNorthBearing(options.projection, mapCenter.value.lng, mapCenter.value.lat));

const gridNorthNote = computed(() => buildGridNorthNote(signedBearing(currentBearing.value)));

const advancedSummary = computed(
  () => describeAdvancedOptions(options, findProjection(options.projection).shortLabel),
);

const routeStyleSummaryText = computed(() => routeStyleSummary(routeStyleSettings.value));

/**
 * Keeps the panel and the preview in step with an edited route style.
 *
 * The preview's route layers were built with the expressions that were current
 * when the feature collection was added, so a colour picked now needs the
 * paint properties setting again — otherwise the sheet on screen disagrees
 * with the PDF it is about to produce until the map style reloads.
 */
const handleRouteStyleChanged = () => {
  routeStyleSettings.value = getRouteStyleSettings();
  applyPrintPreviewRouteStyle(mapInstance.value, store.state.printMap.featureCollectionSourceId);
};

const projectionWarning = computed(() => {
  const definition = findProjection(options.projection);
  if (!definition.metric) return '';
  if (isUsableAt(definition, mapCenter.value.lng, mapCenter.value.lat)) return '';
  return t('print_maps.projection_does_not_cover_area', { projection: definition.shortLabel });
});

const printButtonLabel = computed(() => {
  if (pile.value.length > 1) return t('print_maps.print_count_sheets', { count: pile.value.length });
  if (pile.value.length === 1) return t('print_maps.print_one_sheet');
  return t('print_maps.print_this_sheet');
});

const shortScale = buildShortScale;

/** The sheet the user is currently framing, as GeoJSON. */
function currentSheetFeature(): GeoJSON.Feature<GeoJSON.Polygon> | null {
  const map = mapInstance.value;
  if (!map) return null;
  const center = map.getCenter();

  return sheetPolygon({
    center: { lng: center.lng, lat: center.lat },
    denominator: options.scaleDenominator,
    frameWidthMm: layout.value.frame.width,
    frameHeightMm: layout.value.frame.height,
    bearingDegrees: currentBearing.value,
  });
}

/**
 * Redraws the preview rectangle. Bound to `moveend` — the rectangle is
 * anchored to the map centre, so it only needs to settle once the gesture
 * has finished.
 */
function refreshSheetOverlay(): void {
  const map = mapInstance.value;
  if (!map || !map.isStyleLoaded()) return;

  syncMapCenter();

  // Only meaningful when the user is framing the sheet themselves. For a
  // whole-hike job the server decides the framing, so a box under the
  // crosshair would be telling them something untrue.
  const feature = isCustomArea.value ? currentSheetFeature() : EMPTY_COLLECTION;
  if (!feature) return;

  const sheetSource = map.getSource(SHEET_SOURCE_ID) as GeoJSONSource | undefined;
  if (sheetSource) {
    sheetSource.setData(feature);
  } else {
    map.addSource(SHEET_SOURCE_ID, { type: 'geojson', data: feature });
    map.addLayer({
      id: `${SHEET_SOURCE_ID}-fill`,
      type: 'fill',
      source: SHEET_SOURCE_ID,
      paint: { 'fill-color': '#ffffff', 'fill-opacity': 0.08 },
    });
    map.addLayer({
      id: `${SHEET_SOURCE_ID}-line`,
      type: 'line',
      source: SHEET_SOURCE_ID,
      paint: { 'line-color': '#DB2B35', 'line-width': 3, 'line-dasharray': [3, 2] },
    });
    refreshPileOverlay();
  }
}

function refreshPileOverlay(): void {
  const map = mapInstance.value;
  if (!map || !map.isStyleLoaded()) return;

  const data = outlineCollection(pile.value);
  const pileSource = map.getSource(PILE_SOURCE_ID) as GeoJSONSource | undefined;
  if (pileSource) {
    pileSource.setData(data);
    return;
  }

  map.addSource(PILE_SOURCE_ID, { type: 'geojson', data });
  map.addLayer({
    id: `${PILE_SOURCE_ID}-line`,
    type: 'line',
    source: PILE_SOURCE_ID,
    paint: { 'line-color': '#1a3a8f', 'line-width': 2 },
  });
  map.addLayer({
    id: `${PILE_SOURCE_ID}-label`,
    type: 'symbol',
    source: PILE_SOURCE_ID,
    layout: { 'text-field': ['get', 'label'], 'text-size': 16, 'text-allow-overlap': true },
    paint: { 'text-color': '#1a3a8f', 'text-halo-color': '#ffffff', 'text-halo-width': 2 },
  });
}

// `setBearing` fires `moveend` synchronously, and this runs from `moveend`.
let applyingBearing = false;

function applyBearing(): void {
  const map = mapInstance.value;
  if (!map || applyingBearing) return;

  // Rotate the preview to match the printed sheet, so what the user frames on
  // screen is literally what comes out of the printer.
  //
  // Compared as a shortest turn, not a subtraction: a compass bearing is
  // 0..360 and Mapbox reports its own as -180..180, so a westerly grid
  // convergence (357 here, -3 there) looked like a 360 degree difference.
  // The map was then rotated on every moveend, each rotation firing another
  // moveend, until the stack gave out — which is what picking British
  // National Grid west of its central meridian did.
  const target = signedBearing(currentBearing.value);
  if (Math.abs(bearingDelta(map.getBearing(), target)) <= 0.01) return;

  applyingBearing = true;
  try {
    map.setBearing(target);
  } finally {
    applyingBearing = false;
  }
}

function setPageSize(size: PageSize): void {
  options.pageSize = size;
  nextTick(refreshSheetOverlay);
}

function setOrientation(orientation: Orientation): void {
  options.orientation = orientation;
  nextTick(refreshSheetOverlay);
}

function setScale(value: number | null): void {
  if (value) lastFixedDenominator.value = value;
  options.scaleDenominator = value;
  nextTick(() => {
    refreshSheetOverlay();
    if (value) fitViewToSheet();
  });
}

function onProjectionChange(): void {
  const definition = findProjection(options.projection);
  if (definition.metric) options.gridCrs = definition.gridCrs;
  applyBearing();
  nextTick(refreshSheetOverlay);
}

/** Zoom the preview so the whole sheet outline is comfortably visible. */
function fitViewToSheet(): void {
  const map = mapInstance.value;
  const feature = currentSheetFeature();
  if (!map || !feature) return;

  const ring = feature.geometry.coordinates[0];
  const bounds = ring.reduce(
    (accumulator, coordinate) => accumulator.extend(coordinate as LngLatLike),
    new mapboxgl.LngLatBounds(ring[0] as LngLatLike, ring[0] as LngLatLike),
  );

  map.fitBounds(bounds, { padding: 48, duration: 250, bearing: map.getBearing() });
}

function snapshotOptions(): SheetOptions {
  return { ...options };
}

function addSheet(sheet: Omit<QueuedSheet, 'key'>): void {
  sheetCounter.value += 1;
  pile.value = [...pile.value, { ...sheet, key: `sheet-${sheetCounter.value}` }];
}

function addCurrentSheet(): void {
  const map = mapInstance.value;
  if (!map) return;
  const center = map.getCenter();

  addSheet({
    title: resolvedTitle(),
    center: { lng: center.lng, lat: center.lat },
    bearing: currentBearing.value,
    options: snapshotOptions(),
    sheetMode: 'single',
  });
  flash.value = { type: 'ok', message: t('print_maps.sheet_added') };
}

/**
 * The one action the panel offers, whatever mode is selected.
 *
 * A whole-hike job never goes through the pile: its centre is a placeholder,
 * because the server re-frames every day on that day's own route. Parking it
 * there drew a meaningless coverage box on the map and asked the user to
 * click Print a second time for a job they had already asked for.
 */
async function printNow(): Promise<void> {
  const center = mapInstance.value?.getCenter();
  if (!center) return;

  if (isCustomArea.value) {
    const sheets: QueuedSheet[] = pile.value.length
      ? pile.value
      : [{
          key: 'current',
          title: resolvedTitle(),
          center: { lng: center.lng, lat: center.lat },
          bearing: currentBearing.value,
          options: snapshotOptions(),
          sheetMode: 'single',
        }];

    // A collected pile becomes one document. A lone sheet stays on the
    // original single-sheet path: the output is identical either way, and
    // there is no reason to route the commonest print through a new mode.
    const combine = sheets.length > 1;

    await submitSheets(sheets, {
      clearPile:      pile.value.length > 0,
      combine,
      successMessage: combine
        ? buildCombinedSuccessMessage(sheets.length, pileJobCount(sheets))
        : undefined,
    });
    return;
  }

  const days = source.value?.days.length ?? 0;
  await submitSheets([
    {
      key: printMode.value,
      title: resolvedTitle(),
      center: { lng: center.lng, lat: center.lat },
      bearing: currentBearing.value,
      options: snapshotOptions(),
      sheetMode: printMode.value,
    },
  ], {
    successMessage: printMode.value === 'per_day'
      ? t('print_maps.printing_all_days', { count: days })
      : t('print_maps.printing_whole_route'),
  });
}

function removeSheet(key: string): void {
  pile.value = pile.value.filter((sheet) => sheet.key !== key);
}

function resolvedTitle(): string {
  const trimmed = mapTitle.value?.trim();
  return trimmed || defaultMapTitle || t('print_maps.my_hiiker_map');
}

function printContext(): PrintContext {
  return {
    mapType: resolvePrintMapType(store.state.mapStyles?.currentMapStyle),
    defaultTitle: resolvedTitle(),
    sourceType: source.value?.type ?? null,
    sourceId: source.value?.id ?? null,
    geometry: store.state.printMap.featureCollection,
  };
}

interface SubmitOptions {
  /** Empty the pile once the sheets are away. Only when the pile was sent. */
  clearPile?: boolean;
  /** Send the sheets as pages of one job rather than one job each. */
  combine?: boolean;
  successMessage?: string;
}

async function submitSheets(sheets: QueuedSheet[], options: SubmitOptions = {}): Promise<void> {
  if (!sheets.length || submitting.value) return;

  const isPro = await RevenueCatWrapper.isProOrProPlus();
  if (!isPro) {
    window.location.href = '/pricing';
    return;
  }

  submitting.value = true;
  flash.value = null;

  try {
    const context = printContext();
    const jobs = options.combine ? buildPileVariables(sheets, context) : buildBatchVariables(sheets, context);
    const created = await store.dispatch('printMap/createPrintMaps', { sheets: jobs });

    if (options.clearPile) pile.value = [];
    queueDrawerRef.value?.pushJobs(created.printMaps ?? []);

    // Counted in sheets rather than failed jobs: one combined job can carry a
    // dozen of them, and "1 sheet(s) could not be queued" badly understates it.
    const failures = created.errors ?? [];
    const lostSheets = buildFailedSheetCount(jobs, failures.map((failure) => failure.index));
    flash.value = failures.length
      ? { type: 'error', message: t('print_maps.sheets_could_not_be_queued', { count: lostSheets, error: failures[0].message }) }
      : {
          type: 'ok',
          message: options.successMessage
            ?? t('print_maps.sent_to_print_queue'),
        };
  } catch (error) {
    console.error('Error creating print maps:', error);
    flash.value = { type: 'error', message: t('print_maps.could_not_queue_print') };
  } finally {
    submitting.value = false;
  }
}


const buttonClicked = async (id: number) => {
  switch (id) {
    case 0:
      if (window.user) {
        const element = document.getElementById('map-styles-dialog');
        if (element) new Modal(element).show();
      } else {
        window.onBoardingVue?.launchFor(OnBoardingGoal.MapStyles);
      }
      break;
    case 2: {
      const element = document.getElementById('go-to-location-dialog');
      if (element) new Modal(element).show();
      break;
    }
  }
};

// One-time nudge toward the "Maps" button. The background style is what the printed sheet
// ends up looking like, and the button sits over the map where it is easy to miss. Marked
// as seen the moment it is shown rather than on dismiss, so closing the tab without
// clicking through does not bring it back on the next visit.
const showMapStyleHintOnce = () => {
  if (!shouldShowPrintMapStyleHint()) return;
  // Below 600px the "Printing from your mobile device?" overlay covers the page — do not
  // stack a modal behind it.
  if (window.innerWidth < 600) return;

  const element = document.getElementById('print-map-style-hint-dialog');
  if (!element) return;

  markPrintMapStyleHintSeen();
  new Modal(element).show();
};

/**
 * Leaves the print page for wherever the user came from.
 *
 * The decision is in `printMapBackTarget` — which arrival gets history and
 * which gets a URL is the part worth testing, and it cannot be tested in
 * here.
 */
const goBack = () => {
  const target = printMapBackTarget({
    referrer:      document.referrer,
    origin:        window.location.origin,
    historyLength: window.history.length,
    sourceUrl:     source.value?.url ?? null,
  });

  if (target.kind === 'history') {
    window.history.back();
    return;
  }

  window.location.href = target.url;
};

const handleMapStyleLoaded = async () => {
  refreshSheetOverlay();
  const featureCollection = store.state.printMap.featureCollection;
  if (featureCollection) {
    await store.dispatch('printMap/addFeatureCollection', { featureCollection, fitBounds: false });
  }
};

watch(
  () => [options.showGrid, options.showElevationProfile, options.pageSize, options.orientation],
  () => nextTick(refreshSheetOverlay),
);

// Keeps the preview's heatmap in step with the switch. While a style is still
// loading the `style.load` handler below applies it instead.
watch(
  () => options.showHeatmap,
  (enabled) => {
    if (!window.map?.isStyleLoaded()) return;
    syncPrintHeatmap(window.layerRegistry, enabled);
  },
);

// Keeps the coverage outlines honest however the pile changed — including the
// "Clear" button, which mutates it directly.
watch(pile, () => nextTick(refreshPileOverlay), { deep: true });

watch(
  () => options.hikeDate,
  (value) => {
    if (!value) options.showSunTimes = false;
  },
);

// The route can arrive after mount (it is fetched and added to the store), so
// clear the option if it turns out there is nothing to profile.
watch(hasRoute, (value) => {
  if (!value) options.showElevationProfile = false;
});

/** Applies the (unit-tested) option changes a mode change implies. */
function applyMode(mode: SheetMode, initial = false): void {
  const next = optionsForMode({
    mode,
    hasSource:            Boolean(source.value),
    scaleDenominator:     options.scaleDenominator,
    lastFixedDenominator: lastFixedDenominator.value,
    showInfoSheet:        options.showInfoSheet,
    initial,
  });

  options.scaleDenominator = next.scaleDenominator;
  options.showInfoSheet = next.showInfoSheet;
}

watch(printMode, (mode) => {
  applyMode(mode);
  nextTick(refreshSheetOverlay);
});

onMounted(async () => {
  // Opening the print page from a hike means the hike is what you came to
  // print, so that is the mode you land on.
  printMode.value = availableModes.value[0]?.value ?? 'single';
  applyMode(printMode.value, true);

  store.commit('mapStyles/setPrintMapMode', true);
  await store.dispatch('mapStyles/initialiseStore');

  if (window.innerWidth < 600 && mobileCtaRef.value) {
    mobileCtaRef.value.style.visibility = 'visible';
  }

  const queryParams = getQueryParams(window.location.href);
  const parseNumericParam = (value: unknown): number | undefined => {
    if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
    if (typeof value === 'string') {
      const parsed = Number.parseFloat(value);
      return Number.isFinite(parsed) ? parsed : undefined;
    }
    return undefined;
  };

  const lngFromParams = parseNumericParam(queryParams.lng);
  const latFromParams = parseNumericParam(queryParams.lat);
  const zoomFromParams = parseNumericParam(queryParams.zoom);

  let initialCenter: LngLatLike | undefined;
  let initialZoom: number | undefined;
  if (lngFromParams !== undefined && latFromParams !== undefined) {
    initialCenter = [lngFromParams, latFromParams];
    initialZoom = zoomFromParams ?? 14;
  } else if (zoomFromParams !== undefined) {
    initialZoom = zoomFromParams;
  }

  if (!mapContainerRef.value) {
    console.error('Map container element not found.');
    return;
  }

  const storeInitialSettings = store.getters['printMap/initialMapSettings'];
  window.map = new Map({
    ...storeInitialSettings,
    container: mapContainerRef.value,
    center: initialCenter || storeInitialSettings.center,
    zoom: initialZoom || storeInitialSettings.zoom,
    interactive: true,
  });
  mapInstance.value = window.map;
  window.layerRegistry = new LayerRegistry(window.map);

  window.map.on('load', async () => {
    await store.dispatch('waypoints/initialiseStore');

    // Use the shared helper rather than a local GeolocateControl: MapControls'
    // location button calls `window.geoLocate.permissionAndTrigger()`, which only
    // the helper defines.
    createGeoLocate();

    setTimeout(() => {
      const element = document.querySelector('.mapboxgl-ctrl-geolocate');
      if (element instanceof HTMLElement) element.style.display = 'none';
    }, 100);

    refreshSheetOverlay();
    fitViewToSheet();

    if (window.featureCollection) {
      setTimeout(async () => {
        await store.dispatch('printMap/addFeatureCollection', {
          featureCollection: window.featureCollection,
          fitBounds: true,
        });
      }, 500);
    }

    await store.dispatch('printMap/mapInitialised');

    showMapStyleHintOnce();
  });

  window.map.on('style.load', () => {
    syncPrintHeatmap(window.layerRegistry, options.showHeatmap);
    setTimeout(refreshSheetOverlay, 100);
    setTimeout(() => {
      const element = document.querySelector('.mapboxgl-ctrl-geolocate');
      if (element instanceof HTMLElement) element.style.display = 'none';
    }, 100);
  });

  window.map.on('moveend', () => {
    if (!window.map) return;

    // Redraw before persisting the centre, and never let persistence break
    // the redraw. `SavedCenter.saveCurrentCenter` writes to localStorage,
    // which throws whenever storage is blocked or full (private browsing, for
    // one) — and it used to run first, so a throw skipped the rest of this
    // handler entirely. Panning then did nothing while zooming still worked,
    // because zoom refreshed from its own separate event.
    applyBearing();
    refreshSheetOverlay();

    try {
      SavedCenter.saveCurrentCenter();
    } catch (error) {
      console.warn('Could not persist the map centre', error);
    }
  });
  window.map.on('error', (event) => console.error('Mapbox error:', event));

  const isProOrProPlus = await RevenueCatWrapper.isProOrProPlus();
  let currentStyle = HiikerMapStyle.getCurrentStyle(isProOrProPlus);
  const nonPrintableNames = [HiikerMapStyle.hiking().name, HiikerMapStyle.lidar().name];
  if (nonPrintableNames.includes(currentStyle.name)) {
    currentStyle = HiikerMapStyle.standard();
    currentStyle.saveCurrentStyle();
    store.commit('mapStyles/setCurrentMapStyle', currentStyle);
  }
  window.map.setStyle(currentStyle.printMapUrl());

  emitter.on('map-style-loaded', handleMapStyleLoaded);
  emitter.on('route-style-settings:changed', handleRouteStyleChanged);
});

onBeforeUnmount(() => {
  emitter.off('map-style-loaded', handleMapStyleLoaded);
  emitter.off('route-style-settings:changed', handleRouteStyleChanged);
  if (window.map) {
    window.map.remove();
    window.map = null;
  }
  store.commit('mapStyles/setPrintMapMode', false);
});
</script>

<style scoped>
.print-maps-page { height: 90vh; }
.print-maps-row { height: 90vh; }

.print-left-col {
  max-height: 90vh;
  position: relative;
  height: 100%;
}

.home-stats-bg { z-index: 0; }

.map-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.map-content {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 0;
  overflow: hidden;
}

.map-custom-class, canvas { width: 100%; height: 100%; }

.button-container {
  position: absolute;
  top: 60px;
  right: 10px;
  z-index: 10;
  display: flex;
  gap: 10px;
}

.queue-container {
  position: absolute;
  top: 12px;
  right: 10px;
  z-index: 12;
}

.back-container {
  position: absolute;
  top: 12px;
  left: 10px;
  z-index: 12;
}

/* The map's own furniture is dark translucent pills (see .trail-map-buttons
   in app.scss); this matches it rather than sitting on the map as a light
   card. Restated locally because those rules are scoped to that container. */
.back-button {
  border: none;
  color: #fff;
  background-color: rgba(17, 17, 17, 0.8);
  padding: 0.5rem 0.9rem;
}

.back-button:hover { background-color: rgba(17, 17, 17, 1); color: #fff; }

.back-button:focus-visible {
  outline: 2px solid var(--yellow, #fddf00);
  outline-offset: 2px;
}

.back-button .material-icons { font-size: 1.15rem; }

/* Matches the map's own furniture — the zoom and layer controls are dark
   translucent pills — rather than sitting on it as a white card. */
.sheet-readout {
  position: absolute;
  left: 14px;
  /* Clears the Mapbox wordmark and attribution, which have to stay legible. */
  bottom: 34px;
  z-index: 11;
  background: rgba(17, 17, 17, 0.82);
  backdrop-filter: blur(6px);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 12px;
  padding: 0.5rem 0.75rem 0.55rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
  max-width: 300px;
}

.readout-scale {
  font-size: 0.95rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  line-height: 1.2;
  color: var(--yellow, #fddf00);
}

.readout-facts {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0.05rem 0.5rem;
  margin: 0.3rem 0 0;
  font-size: 0.72rem;
  line-height: 1.3;
}

.readout-facts dt {
  color: rgba(255, 255, 255, 0.5);
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-size: 0.65rem;
  padding-top: 0.06rem;
}

.readout-facts dd {
  margin: 0;
  color: rgba(255, 255, 255, 0.92);
}

.print-right-col {
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  border-left: 1px solid rgba(255, 255, 255, 0.15);
  padding: 0;
  min-height: 0;
}

.options-scroll {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  padding: 1rem 1rem 0.5rem;
  /* Fades the cut-off edge so it reads as "more below" rather than content
     that has simply been chopped by the action bar. */
  mask-image: linear-gradient(to bottom, #000 calc(100% - 14px), transparent 100%);
}

/* What to print, chosen first. */
.mode-card {
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 10px;
  padding: 0.7rem 0.75rem;
  background: rgba(255, 255, 255, 0.05);
  margin-bottom: 1rem;
}

.mode-options { display: flex; flex-direction: column; gap: 0.3rem; }

.mode-option {
  display: flex;
  gap: 0.55rem;
  align-items: flex-start;
  padding: 0.45rem 0.55rem;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 8px;
  cursor: pointer;
}

.mode-option.active {
  border-color: var(--yellow, #ffd60a);
  background: rgba(255, 214, 10, 0.12);
}

.mode-option input { margin-top: 0.2rem; flex: 0 0 auto; }
.mode-label { display: block; font-size: 0.85rem; font-weight: 600; }
.mode-hint { display: block; font-size: 0.72rem; color: rgba(255, 255, 255, 0.6); line-height: 1.25; }

.action-hint {
  font-size: 0.72rem;
  color: rgba(255, 255, 255, 0.6);
  margin: 0.4rem 0 0;
  text-align: center;
}

/* The options panel sits on the app's dark body, so its supporting text has
   to be light — mid-greys vanish against it. */
.option-heading {
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: rgba(255, 255, 255, 0.72);
  font-weight: 700;
  margin-bottom: 0.4rem;
}

.option-hint { font-size: 0.75rem; color: rgba(255, 255, 255, 0.65); margin-bottom: 0.5rem; }
.option-warning { font-size: 0.75rem; color: #f0b429; }

.option-flash { font-size: 0.78rem; margin: 0.45rem 0 0; text-align: center; }

/* Bootstrap's switches default to its own blue, which is not a HiiKER
   colour and reads as unstyled next to the yellow controls around them.
   The knob images are inline SVGs, so both states have to be restated. */
.form-switch .form-check-input {
  width: 2.1rem;
  height: 1.15rem;
  margin-top: 0.1rem;
  background-color: rgba(255, 255, 255, 0.16);
  border-color: rgba(255, 255, 255, 0.22);
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='rgba%28255,255,255,0.75%29'/%3e%3c/svg%3e");
  cursor: pointer;
  transition: background-color 0.15s ease-in-out, border-color 0.15s ease-in-out;
}

.form-switch .form-check-input:checked {
  background-color: var(--yellow, #fddf00);
  border-color: var(--yellow, #fddf00);
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='%2317181D'/%3e%3c/svg%3e");
}

.form-switch .form-check-input:focus {
  border-color: rgba(253, 223, 0, 0.6);
  box-shadow: 0 0 0 0.2rem rgba(253, 223, 0, 0.25);
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='rgba%28255,255,255,0.75%29'/%3e%3c/svg%3e");
}

.form-switch .form-check-input:checked:focus {
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='%2317181D'/%3e%3c/svg%3e");
}

.form-switch .form-check-input:disabled {
  background-color: rgba(255, 255, 255, 0.07);
  border-color: rgba(255, 255, 255, 0.1);
  opacity: 1;
  cursor: not-allowed;
}

.form-check-label { cursor: pointer; font-size: 0.86rem; }
.form-check-input:disabled ~ .form-check-label { color: rgba(255, 255, 255, 0.45); cursor: not-allowed; }
.form-check.form-switch { padding-left: 2.7rem; margin-bottom: 0.35rem; }
.form-check.form-switch .form-check-input { margin-left: -2.7rem; }
.advanced-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  width: 100%;
  text-align: left;
  padding: 0.55rem 0.7rem;
  margin-bottom: 0.75rem;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.04);
  color: #fff;
}

.advanced-toggle:hover {
  border-color: rgba(255, 255, 255, 0.24);
  background: rgba(255, 255, 255, 0.07);
}

.advanced-toggle:focus-visible {
  outline: 2px solid var(--yellow, #fddf00);
  outline-offset: 2px;
}

.advanced-toggle-text { min-width: 0; }

.advanced-title {
  display: block;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.advanced-summary {
  display: block;
  font-size: 0.72rem;
  color: rgba(255, 255, 255, 0.62);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.advanced-chevron {
  flex: 0 0 auto;
  font-size: 1.3rem;
  color: rgba(255, 255, 255, 0.7);
  transition: transform 0.18s ease;
}

.advanced-chevron.open { transform: rotate(180deg); }

/* The editor brings its own spacing; this just keeps it clear of the fold
   button above and the mode card below. */
.route-style-body { margin-bottom: 0.75rem; }

.option-flash.ok { color: #6ed58a; }
.option-flash.error { color: #ff8a8f; }

/* Chrome paints a date field's calendar button and its drop-down picker from
   the element's colour scheme, not from our CSS — on a dark panel that left a
   black glyph on a black input and would have opened a white calendar. */
.print-right-col input[type='date'] {
  color-scheme: dark;
}

.scale-chip { padding: 0.15rem 0.5rem; font-size: 0.75rem; }

/* Fitting is on, so no chip is the answer — but they stay clickable, and
   clicking one is how you switch to a fixed scale. */
.scale-buttons.is-fitted { opacity: 0.45; }
.scale-buttons.is-fitted:hover { opacity: 0.75; }
.scale-buttons { transition: opacity 0.15s ease; }

.source-card {
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 10px;
  padding: 0.6rem 0.7rem;
  background: rgba(255, 255, 255, 0.06);
}

.source-name { font-weight: 600; font-size: 0.9rem; }
.source-meta { font-size: 0.75rem; color: rgba(255, 255, 255, 0.65); }

.pile-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 8px;
  padding: 0.4rem 0.5rem;
  margin-bottom: 0.35rem;
}

.pile-index {
  width: 1.4rem;
  height: 1.4rem;
  border-radius: 50%;
  background: #1a3a8f;
  color: #fff;
  font-size: 0.72rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
}

.pile-body { min-width: 0; flex: 1; }
.pile-title { font-size: 0.82rem; font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.pile-meta { font-size: 0.7rem; color: rgba(255, 255, 255, 0.62); }

.print-actions {
  flex: 0 0 auto;
  padding: 0.75rem 1rem 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.15);
  background: var(--dark, #111);
}

.mobileCTA {
  position: absolute;
  z-index: 10000;
  background: #202020fa;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  visibility: hidden;
}
</style>
